import { Box, Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import { InvoiceProfileService } from "subModule/src/core/services";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { CreditNoteResponse, GetCreditNoteByIdResponse } from "subModule/src/services/invoice/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import CreditIcon from "@mui/icons-material/CreditScore";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
const ViewSalesCreditNote: React.FC = () => {
    const params = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
        sectionLabelKey: "customers",
        moduleLabelKey: "creditNotes",
        moduleHref: "/sales-credit-notes",
        entityFallbackLabelKey: "creditNote",
        isEdit: !!params?.id,
        action: "view",
    });
    const branding = useSelector(brandingInfo);
    const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
    const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [creditNote, setCreditNote] = useState<CreditNoteResponse>();
    useEffect(() => {
        if (loading) fetchCreditNote();
    }, []);
    const fetchCreditNote = () => {
        InvoiceProfileService.getCreditNoteById(Number(params?.id)).then((res: GetCreditNoteByIdResponse) => {
            if (res.success) {
                setCreditNote(res?.data || {});
                captureLabel(
                    res?.data?.creditNoteNumber != null
                        ? String(res.data.creditNoteNumber)
                        : null,
                );
                setLoading(false);
            } else {
                setLoading(false);
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const columns = [
        { id: 'description', label: t('description'), numeric: false },
        { id: 'quantity', label: t('quantity'), numeric: false },
        { id: 'unitPrice', label: t('price'), numeric: false },
        { id: 'taxAmount', label: t('tax'), numeric: false },
        { id: 'totalAmount', label: t('total'), numeric: false },
    ];
    return (
        <>
            <Box className="hide-on-print">
                <ComponentHeaderTwo
                    breadcrumbItems={breadcrumbItems}
                    showBtn={true}
                    showSaveBtn={false}
                    onClick={() => navigate("/sales-credit-notes")}
                    onSubmit={() => {}}
                    heading={t("creditNotes")}
                    icon={<CreditIcon />}
                    btnText={"back"}
                    btnType={"back"}
                    showCreateBtn={false}
                />
            </Box>
            {loading ? <Loader /> : <Box>
            <Box className='max-w-[1100px] bg-white py-[10px] px-[20px] rounded-[8px] block mx-auto hide-on-print mb-4'>
                <Box className='flex justify-between items-center' id='form'>
                    <Typography className="font-Saira text-[12px] font-semibold" style={{ color: themeColors?.primary }}>{t('CREDITNOTE')}</Typography>
                    <Box className='flex gap-2'>
                        <Button className="secondary" variant="contained" style={{ color: themeColors?.primary }} disableElevation onClick={() => navigate(-1)}>{t('back')}</Button>
                        <Button className="primary" variant="contained" style={{ background: themeColors?.primary }} disableElevation onClick={() => window.print()}>{t('Print')}</Button>
                    </Box>
                </Box>
            </Box>
            <Box id="print-root" className='max-w-[1100px] bg-white p-[20px] rounded-[8px] block mx-auto'>
                <Box className='flex justify-between mb-2'>
                    <Box>
                        <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('Client')}: </span>
                            <span className="font-bold">{creditNote?.attributes?.customerName || ''}</span>
                        </Typography>
                        {creditNote?.attributes?.stationName ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('station')}: </span>
                            <span className="font-bold">{creditNote?.attributes?.stationName || ''}</span>
                        </Typography> : ''}
                        {creditNote?.attributes?.makeTitle ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('vehicleName')}: </span>
                            <span className="font-bold">{creditNote?.attributes?.makeTitle || ''}</span>{' '}
                            <span className="font-bold">{creditNote?.attributes?.modelTitle || ''}</span>{' '}
                            {creditNote?.attributes?.modelEngineTitle ? <span className="font-bold">{creditNote?.attributes?.modelEngineTitle || ''}</span> : ''}
                        </Typography> : ''}
                        {creditNote?.vehiclePlateNo ? <Typography className="font-Saira text-[12px] text-left">
                            <span className="italic font-medium">{t('vehiclePlateNo')}: </span>
                            <span className="font-bold">{creditNote?.vehiclePlateNo || ''}</span>
                        </Typography> : ''}
                    </Box>
                    <Box className='flex flex-col gap-1'>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('CREDITNOTE')} # </span>
                                <span className="font-semibold">{creditNote?.creditNoteNumber || ''}</span>
                            </Typography>
                        </Box>
                        <Box className='block py-1 px-2 rounded-[8px]' style={{ background: themeColors?.appBg }}>
                            <Typography className="font-Saira text-[12px]">
                                <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('invoice')} # </span>
                                <span className="font-semibold">{creditNote?.attributes?.invoiceNo || ''}</span>
                            </Typography>
                        </Box>
                        {creditNote?.referenceNumber ?<Typography className="font-Saira text-[12px] text-end font-semibold">
                            <span className="font-bold" style={{ color: themeColors?.secondary }}>{t('referenceNo')} </span>
                            <span className="font-semibold">{creditNote?.referenceNumber || ''}</span>
                        </Typography> : null}
                    </Box>
                </Box>
                <Box className='flex justify-between items-center py-3 px-2 rounded-[8px] mb-2' style={{ background: themeColors?.appBg }}>
                    <Typography className="font-Saira text-[12px] text-left">
                        <span className="italic font-medium">{t('VAT Number')}</span>
                        <span> : </span>
                        <span className="font-bold">{creditNote?.attributes?.customerTaxNumber || ''}</span>
                    </Typography>
                    <Box className="flex gap-3 items-center">
                        <Typography className="font-Saira text-[12px] text-left block py-1 px-3 rounded-[8px]" style={{ background: themeColors?.secondary + '33' }}>
                            <span className="italic font-medium">{t('Date')}</span>
                            <span> : </span>
                            <span className="font-bold">{creditNote?.creditNoteDate?.split('T')[0] || ''}</span>
                        </Typography>
                    </Box>
                </Box>
                {creditNote?.creditNoteItems && creditNote?.creditNoteItems?.length ? <>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#333843] text-left mb-2">{t('creditNoteItems')}</Typography>
                    <Box className='w-[100%] mb-2' id="inventorytable">
                        <TableContainer>
                            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'small'}>
                                <TableHead>
                                    <TableRow sx={{ borderBottom: `1px solid #B8BDCB` }}>
                                        {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                            <TableCell
                                                key={column.id}
                                                align={column.numeric ? 'right' : 'left'}
                                                padding={'normal'}
                                                sortDirection={false}
                                                className="font-bold text-center"
                                            >{column.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ '& tr:nth-child(odd)': { background: `transparent !important` }, '& tr': { borderBottom: `1px solid #B8BDCB` }, '& tr:last-child': { borderBottom: 0 } }}>
                                    {(creditNote?.creditNoteItems || []).map((row, index) => {
                                        const labelId = `enhanced-table-checkbox-${index}`;
                                        return (
                                            <TableRow hover tabIndex={-1} key={row.id} sx={{ height: '36px' }}>
                                                {columns.filter((column) => column.id !== 'itemId' && column.id !== 'itemCode' && column.id !== 'unitId').map((column) => (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.numeric ? 'right' : 'left'}
                                                        padding={'normal'}
                                                        component={column.id === 'itemName' ? 'th' : undefined}
                                                        id={column.id === 'itemName' ? labelId : undefined}
                                                        scope={column.id === 'itemName' ? 'row' : undefined}
                                                        className="text-center"
                                                    >{column.id === 'description' ? row?.itemTitle || row?.deliveryNoteNo
                                                        : column.id === 'unitPrice' ? row?.unitPrice || row?.subtotalAmount
                                                        : (row as any)[column.id]
                                                    }</TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </> : null}
                <Box className='flex justify-end mt-4 mb-3'>
                    <Box className="w-[360px]">
                        <Divider className="bg-[#AEB3C4] mb-2" />
                        <Box className='flex justify-between mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('subTotal')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(creditNote?.amount || 0) || 0}</span>
                                </p>
                            </Typography>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('tax')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(creditNote?.taxAmount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                        <Box className='flex justify-end mb-[5px]'>
                            <Typography className="font-Saira text-[12px] text-left flex">
                                <span className="font-medium">{t('total')}: </span>
                                <p className="font-bold ms-1">
                                    <span className="uppercase ms-1 font-semibold" style={{ color: themeColors?.primary }}>{loggedInUserCurrency}</span>
                                    <span className="italic font-bold ms-1">{new Intl.NumberFormat('en-US', {}).format(creditNote?.totalAmount || 0) || 0}</span>
                                </p>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {creditNote?.reason ? <Box>
                    <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">{t("comment")}</Typography>
                    <Typography className="font-Saira text-[12px] text-[#171826] text-left">{creditNote?.reason}</Typography>
                </Box> : null}
            </Box>
        </Box>}
        </>
    )
}

export default ViewSalesCreditNote;